import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MarkerIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/components/capstone/base';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import useReverseGeocode, {
  type ReverseGeocodeLocationData,
} from '@rsos/maps/interface/reverseGeocode/useReverseGeocode';
import { StateWithPsaps } from '@rsos/reduxDataStore';
import { formatNormalizedAddress } from '@rsos/reduxDataStore/src/features/emergencyCalls/normalizedAddress';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkInternationalSupport from '@rsos/utils/checkInternationalSupport';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';

const emptyAddressObject = {
  home: '',
  street: '',
  city: '',
  region: '',
  postal_code: '',
};

export const LocationInformation = () => {
  const hasInternationalSupport = useSelector((state: StateWithPsaps) =>
    checkInternationalSupport({ psaps: state.psaps }),
  );
  const selectedCall = useSelectedCall();
  const mostRecentLocations = getMostRecentLocations(selectedCall);
  const isSecondaryLocation = Boolean(
    !mostRecentLocations.mostRecentLocation &&
      mostRecentLocations.mostRecentSecondaryLocation,
  );
  const mostRecentLocation =
    mostRecentLocations.mostRecentLocation ||
    mostRecentLocations.mostRecentSecondaryLocation;
  const mostRecentLocationIndex = isSecondaryLocation
    ? mostRecentLocations.mostRecentSecondaryLocationIndex
    : mostRecentLocations.mostRecentLocationIndex;

  const rgAddress = mostRecentLocation?.rgAddress;
  const address_single_line = mostRecentLocation?.address_single_line;
  const addressObject = mostRecentLocation?.addressObject || emptyAddressObject;
  const cachedAddress = formatNormalizedAddress(
    mostRecentLocation?.normalizedAddress,
  );

  const service = useWhichReverseGeocodeService();

  const { isReverseGeocoding } = useReverseGeocode({
    location: mostRecentLocation as ReverseGeocodeLocationData | null,
    locationIndex: mostRecentLocationIndex ?? undefined,
    isSecondaryLocation: isSecondaryLocation,
    service,
  });

  const displayAddress = useMemo(() => {
    if (cachedAddress) return cachedAddress;

    const { home, street, city, region, postal_code } = addressObject;

    if (address_single_line) {
      return address_single_line;
    } else if (hasInternationalSupport && rgAddress) {
      return rgAddress;
    } else {
      return `${home ? `${home} ` : ''}${street ? `${street}, ` : ''}${
        city ? `${city}, ` : ''
      }${region} ${postal_code}`.trim();
    }
  }, [
    address_single_line,
    cachedAddress,
    rgAddress,
    addressObject,
    hasInternationalSupport,
  ]);

  if (!displayAddress && !isReverseGeocoding) {
    return null;
  }

  return (
    <Box horizontal align="center" marginTop={2}>
      <MarkerIcon color="primaryText" width={12} height={12} />
      <Text
        color="primaryText"
        marginLeft={2}
        size="normal"
        lineHeight="normal"
      >
        {isReverseGeocoding && !displayAddress ? 'Loading...' : displayAddress}
      </Text>
    </Box>
  );
};
